export const getImageFamilyKey = (src = "") =>
  src
    .split("?")[0]
    .replace(/(?:-\d+)+(\.[a-zA-Z0-9]+)$/i, "$1");

export const getProductColor = (product) => {
  const colorAttribute = product?.attributes?.find(
    (attribute) => attribute.name?.toLowerCase() === "color"
  );

  return (
    colorAttribute?.terms?.[0]?.name ||
    colorAttribute?.options?.[0] ||
    "Standard"
  );
};

export const getProductFamilyKey = (product) => {
  const imageKeys = (product?.images || [])
    .map((image) => getImageFamilyKey(image.src))
    .filter(Boolean)
    .sort();

  if (imageKeys.length > 0) {
    return imageKeys.join("|");
  }

  return `product-${product?.id || product?.slug || "unknown"}`;
};

export const groupProductsByFamily = (products = []) => {
  const families = new Map();

  products.forEach((product) => {
    const familyKey = getProductFamilyKey(product);
    const existing = families.get(familyKey);

    if (existing) {
      existing.familyVariants.push(product);
      return;
    }

    families.set(familyKey, {
      ...product,
      familyKey,
      familyVariants: [product]
    });
  });

  return Array.from(families.values());
};

// -----------------------------------------------------------------
// PAGINATED FAMILY CANDIDATE DISCOVERY & CACHING
// -----------------------------------------------------------------

const categoryProductsCache = new Map();

export const clearCategoryProductsCache = () => {
  categoryProductsCache.clear();
};

export const fetchCategoryProductsPaginated = async (categoryId, fetchFn = typeof fetch !== "undefined" ? fetch : null) => {
  if (!categoryId || !fetchFn) return [];

  if (categoryProductsCache.has(categoryId)) {
    return categoryProductsCache.get(categoryId);
  }

  const allProducts = [];
  let page = 1;
  const perPage = 100;
  let hasMore = true;

  while (hasMore && page <= 15) { // Safety ceiling of 15 pages (1500 items)
    try {
      const url = `https://data.lacegota.in/wp-json/wc/store/v1/products?category=${categoryId}&per_page=${perPage}&page=${page}`;
      const res = await fetchFn(url);
      if (!res || !res.ok) break;

      const data = await res.json();
      if (!Array.isArray(data) || data.length === 0) {
        hasMore = false;
        break;
      }

      allProducts.push(...data);
      if (data.length < perPage) {
        hasMore = false;
      } else {
        page++;
      }
    } catch (err) {
      console.error(`Error fetching category ${categoryId} page ${page}:`, err);
      break;
    }
  }

  if (allProducts.length > 0) {
    categoryProductsCache.set(categoryId, allProducts);
  }
  return allProducts;
};

export const fetchFamilyVariants = async (targetProduct, fetchFn = typeof fetch !== "undefined" ? fetch : null) => {
  if (!targetProduct) return [];
  const targetKey = getProductFamilyKey(targetProduct);

  const catId = targetProduct.categories?.[0]?.id;
  let candidateProducts = [];

  if (catId && fetchFn) {
    candidateProducts = await fetchCategoryProductsPaginated(catId, fetchFn);
  }

  // Fallback if no category or category fetch returns empty
  if (candidateProducts.length === 0 && fetchFn) {
    try {
      const res = await fetchFn("https://data.lacegota.in/wp-json/wc/store/v1/products?per_page=100");
      if (res && res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          candidateProducts = data;
        }
      }
    } catch (err) {
      console.error("Error fetching fallback products:", err);
    }
  }

  const familyMap = new Map();
  const allCandidates = [targetProduct, ...candidateProducts];

  allCandidates.forEach((item) => {
    if (item && item.id && !familyMap.has(item.id)) {
      if (getProductFamilyKey(item) === targetKey) {
        familyMap.set(item.id, item);
      }
    }
  });

  return Array.from(familyMap.values());
};
